import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ErrorOutline } from "@mui/icons-material";
import { updateStagedUser } from "redux/actions/users/usersData";
import { getStyles } from "utils/functions/user";
import {
  getCodeText,
  userDesignations,
  userRoles,
  userTypes,
} from "utils/shared/variables";
import { setUsersSliceData } from "redux/features/users/usersSlice";
import { listClinics } from "redux/actions/users/usersData";

// Styled Select
const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiSelect-select": {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    position: "relative",
  },
}));

// Fields that are mandatory
const mandatoryFields = [
  "first_name",
  "last_name",
  "email",
  "role",
  "user_type",
  "organization_name",
];

// Reusable helper to display errors properly
const getFieldError = (errors, fieldName) => {
  const fieldErrors = errors?.[fieldName];

  if (!fieldErrors || fieldErrors.length === 0) return null;

  // Join multiple errors with commas if needed
  const formattedError = Array.isArray(fieldErrors)
    ? fieldErrors.join(", ")
    : fieldErrors;

  const isMandatory = mandatoryFields.includes(fieldName);

  return (
    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <ErrorOutline style={{ fontSize: "18px" }} />
      {formattedError}
    </span>
  );
};

const UserBulkUploadEditForm = () => {
  const { Id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // Redux Selectors
  const {
    userBulkUploadEditNpi,
    userBulkUploadEditStatus,
    userBulkUploadEditEmail,
    userBulkUploadEditRegion,
    userBulkUploadEditProfessionalDesignation,
    userBulkUploadEditUserType,
    userBulkUploadEditOrganization,
    userBulkUploadEditClinic,
    userBulkUploadEditPersonalEmail,
    userBulkUploadEditFirstName,
    userBulkUploadEditRole,
    userBulkUploadEditLastName,
    userBulkUploadEditPhoneNumber,
    userBulkUploadEditErrorList,
    userBulkEditSaveLoading,
    userFilters,
    isUserFiltersLoading,
  } = useSelector((state) => state.users);
  const [clinics, setClinics] = useState([]);
  const [clinicLoading, setClinicLoading] = useState(false);
  const [searchTxt, setSearchTxt] = useState("");
  const userOrganization = useSelector((e) => e.sidebar.organisation);

  const [errors, setErrors] = useState({});

  const getEnabledFields = () => {
    let editFields = [];
    if (userBulkUploadEditStatus) {
      editFields = [
        "npi",
        "region",
        "professional_designation",
        "premium",
        "clinic_name",
        "personal_email",
        "role",
        "first_name",
        "last_name",
        "phone_no",
      ];
    } else {
      editFields = [
        "email",
        "npi",
        "region",
        "professional_designation",
        "premium",
        "organization_name",
        "clinic_name",
        "personal_email",
        "role",
        "first_name",
        "last_name",
        "phone_no",
      ];
    }
    return editFields;
  };
  const enabledFields = getEnabledFields();

  const handleInputChange = (event, mtype) => {
    const { value } = event.target;
    // Update field value in redux
    dispatch(setUsersSliceData({ type: mtype, data: value }));

    // Map local mtype → API field name for errors
    const fieldMapping = {
      userBulkUploadEditClinic: "clinic_name",
      userBulkUploadEditFirstName: "first_name",
      userBulkUploadEditLastName: "last_name",
      userBulkUploadEditEmail: "email",
      userBulkUploadEditPersonalEmail: "personal_email",
      userBulkUploadEditPhoneNumber: "phone_no",
      userBulkUploadEditNpi: "npi",
      userBulkUploadEditRegion: "region",
      userBulkUploadEditRole: "role",
      userBulkUploadEditUserType: "premium",
      userBulkUploadEditProfessionalDesignation: "professional_designation",
      userBulkUploadEditOrganization: "organization_name",
    };

    // Get correct field key for error removal
    const apiField = fieldMapping[mtype];

    // Remove error for this field if exists
    if (apiField && userBulkUploadEditErrorList?.[apiField]) {
      const updatedErrors = { ...userBulkUploadEditErrorList };
      delete updatedErrors[apiField];

      dispatch(
        setUsersSliceData({
          type: "userBulkUploadEditErrorList",
          data: updatedErrors,
        })
      );
    }
  };

  //  Utility function to sanitize data
  const sanitizeData = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() || null : (value ?? null),
      ])
    );
  };

  const handleSubmit = () => {
    //  Collect required fields for validation
    const requiredFields = {
      first_name: userBulkUploadEditFirstName,
      last_name: userBulkUploadEditLastName,
      email: userBulkUploadEditEmail,
      organization_name: userBulkUploadEditOrganization,
      role: userBulkUploadEditRole,
      user_type: userBulkUploadEditUserType,
    };

    //  Validate required fields
    const newErrors = Object.fromEntries(
      Object.entries(requiredFields)
        .filter(([_, value]) => !value || value.toString().trim() === "")
        .map(([key]) => [key, `${key.replace(/_/g, " ")} is required`])
    );

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields.");
      return;
    }
    //  Prepare sanitized data
    const updatedUserData = sanitizeData({
      first_name: userBulkUploadEditFirstName,
      last_name: userBulkUploadEditLastName,
      email: userBulkUploadEditEmail,
      personal_email: userBulkUploadEditPersonalEmail,
      phone_no: userBulkUploadEditPhoneNumber,
      npi: userBulkUploadEditNpi,
      clinic_name: userBulkUploadEditClinic,
      region: userBulkUploadEditRegion,
      role: userBulkUploadEditRole,
      premium: userBulkUploadEditUserType,
      professional_designation: userBulkUploadEditProfessionalDesignation,
      organization_name: userBulkUploadEditOrganization,
    });

    //  API call
    dispatch(updateStagedUser({ updatedUserData, Id })).then((res) => {
      if (res?.payload?.status === 200) {
        navigate("/home/users/bulk/upload/step2");
      } else if (res?.payload?.errors) {
        dispatch(
          setUsersSliceData({
            type: "userBulkUploadEditErrorList",
            data: res.payload.errors,
          })
        );
        toast.error("Errors found. Please fix the highlighted errors.");
      }
    });
  };

  useEffect(() => {
    if (!userBulkUploadEditOrganization) return;
    setClinicLoading(true);
    dispatch(
      listClinics({
        userOrganization,
        page: 1,
        page_size: 10,
        search: searchTxt?.length > 2 ? searchTxt : "",
        filter_org: userBulkUploadEditOrganization,
      })
    )
      .then((response) => {
        if (response.meta.requestStatus === "fulfilled") {
          setClinics(response.payload.results);
        }
      })
      .finally(() => setClinicLoading(false));
  }, [userBulkUploadEditOrganization, searchTxt, userOrganization, dispatch]);

  return (
    <Box py={3} width="80%">
      <Grid container spacing={3}>
        {/* Organization */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">Organization *</Typography>
          <TextField
            name="userBulkUploadEditOrganization"
            fullWidth
            value={userBulkUploadEditOrganization || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditOrganization");
            }}
            error={!!userBulkUploadEditErrorList?.organization_name}
            helperText={getFieldError(
              userBulkUploadEditErrorList,
              "organization_name"
            )}
            size="small"
            disabled={
              enabledFields
                ? !enabledFields.includes("organization_name")
                : false
            }
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>
        <Grid item xs={12} md={4}></Grid>

        {/* First Name */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">First Name *</Typography>
          <TextField
            name="userBulkUploadEditFirstName"
            fullWidth
            value={userBulkUploadEditFirstName || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditFirstName");
            }}
            error={
              !!userBulkUploadEditErrorList?.first_name || !!errors.first_name
            }
            helperText={
              getFieldError(userBulkUploadEditErrorList, "first_name") ||
              errors.first_name
            }
            size="small"
            disabled={
              enabledFields ? !enabledFields.includes("first_name") : false
            }
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">Last Name *</Typography>
          <TextField
            name="userBulkUploadEditLastName"
            fullWidth
            value={userBulkUploadEditLastName || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditLastName");
            }}
            error={
              !!userBulkUploadEditErrorList?.last_name || !!errors.last_name
            }
            helperText={
              getFieldError(userBulkUploadEditErrorList, "last_name") ||
              errors.last_name
            }
            size="small"
            disabled={
              enabledFields ? !enabledFields.includes("last_name") : false
            }
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>

        {/* Email */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">Email Address *</Typography>
          <TextField
            name="userBulkUploadEditEmail"
            fullWidth
            type="email"
            value={userBulkUploadEditEmail || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditEmail");
            }}
            error={!!userBulkUploadEditErrorList?.email || !!errors.email}
            helperText={
              getFieldError(userBulkUploadEditErrorList, "email") ||
              errors.email
            }
            size="small"
            disabled={enabledFields ? !enabledFields.includes("email") : false}
          />
        </Grid>

        {/* Personal Email */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            Personal Email Address{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <TextField
            name="userBulkUploadEditPersonalEmail"
            fullWidth
            type="email"
            value={userBulkUploadEditPersonalEmail || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditPersonalEmail");
            }}
            error={!!userBulkUploadEditErrorList?.personal_email}
            helperText={getFieldError(
              userBulkUploadEditErrorList,
              "personal_email"
            )}
            size="small"
            disabled={
              enabledFields ? !enabledFields.includes("personal_email") : false
            }
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>
        {/* Mobile Number */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            Mobile Number{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <TextField
            name="userBulkUploadEditPhoneNumber"
            fullWidth
            value={userBulkUploadEditPhoneNumber || ""}
            onChange={(event) => {
              // Only allow digits and limit to 10
              const value = event.target.value.replace(/\D/g, "").slice(0, 15);
              event.target.value = value;
              handleInputChange(event, "userBulkUploadEditPhoneNumber");
            }}
            inputProps={{
              inputMode: "numeric",
              maxLength: 15,
            }}
            error={!!userBulkUploadEditErrorList?.phone_no}
            helperText={getFieldError(userBulkUploadEditErrorList, "phone_no")}
            size="small"
            disabled={
              enabledFields ? !enabledFields.includes("phone_no") : false
            }
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>
        <Grid item xs={12} md={4}></Grid>
        {/* NPI */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            NPI{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <TextField
            name="userBulkUploadEditNpi"
            fullWidth
            value={userBulkUploadEditNpi || ""}
            onChange={(event) => {
              const onlyNumbers = event.target.value.replace(/\D/g, "");
              handleInputChange(
                { target: { value: onlyNumbers } },
                "userBulkUploadEditNpi"
              );
            }}
            error={!!userBulkUploadEditErrorList?.npi}
            helperText={getFieldError(userBulkUploadEditErrorList, "npi")}
            size="small"
            disabled={enabledFields ? !enabledFields.includes("npi") : false}
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>
        <Grid item xs={12} md={4}></Grid>

        {/* Clinic */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            Clinic{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <Autocomplete
            fullWidth
            disabled={
              enabledFields ? !enabledFields.includes("clinic_name") : false
            }
            options={clinics.map((c) => c.name)} // only names as options
            freeSolo // allow values not in the options list
            value={userBulkUploadEditClinic || ""} // show stored value
            onChange={(_, value) => {
              handleInputChange(
                { target: { value } },
                "userBulkUploadEditClinic"
              );
            }}
            inputValue={searchTxt}
            onInputChange={(_, newInput, reason) => {
              // Only handle actual user input, not programmatic changes
              if (reason === "input") {
                setSearchTxt(newInput);
                handleInputChange(
                  { target: { value: newInput } },
                  "userBulkUploadEditClinic"
                );
              } else if (reason === "reset") {
                // When autocomplete resets (on blur/select), just update search text
                setSearchTxt(newInput);
              }
            }}
            loading={clinicLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={!!userBulkUploadEditErrorList?.clinic_name}
                helperText={userBulkUploadEditErrorList?.clinic_name || ""}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clinicLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Region */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            Region{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <TextField
            name="userBulkUploadEditRegion"
            fullWidth
            value={userBulkUploadEditRegion || ""}
            onChange={(event) => {
              handleInputChange(event, "userBulkUploadEditRegion");
            }}
            error={!!userBulkUploadEditErrorList?.region}
            helperText={getFieldError(userBulkUploadEditErrorList, "region")}
            size="small"
            disabled={enabledFields ? !enabledFields.includes("region") : false}
          />
        </Grid>
        <Grid item xs={12} md={4}></Grid>

        {/* Role */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">Role *</Typography>
          <FormControl
            fullWidth
            error={!!userBulkUploadEditErrorList?.role || !!errors.role}>
            <StyledSelect
              name="userBulkUploadEditRole"
              value={userBulkUploadEditRole || ""}
              onChange={(event) => {
                handleInputChange(event, "userBulkUploadEditRole");
              }}
              size="small"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "";
                return getCodeText("role", selected); // Use selected directly
              }}>
              {userRoles?.map((type) => (
                <MenuItem
                  key={type.code}
                  value={getCodeText("role", type.code)}
                  style={getStyles(type.code, userBulkUploadEditRole, theme)}>
                  {getCodeText("role", type.code)} {/* Correctly show text */}
                </MenuItem>
              ))}
            </StyledSelect>
            <FormHelperText>
              {getFieldError(userBulkUploadEditErrorList, "role") ||
                errors.role}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* User Type */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">User Type *</Typography>
          <FormControl
            fullWidth
            error={!!userBulkUploadEditErrorList?.premium || !!errors.premium}>
            <StyledSelect
              name="userBulkUploadEditUserType"
              value={userBulkUploadEditUserType || ""}
              onChange={(event) =>
                handleInputChange(event, "userBulkUploadEditUserType")
              }
              size="small"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "";
                // try to map code/text to display text
                const mapped = userTypes.find(
                  (type) => type.code === selected || type.text === selected
                );
                return mapped ? mapped.text : selected; // fallback to raw text
              }}
              disabled={
                enabledFields ? !enabledFields.includes("premium") : false
              }>
              {userTypes?.map((type) => {
                return (
                  <MenuItem key={type} value={type.text}>
                    {getCodeText("userType", type.code)}
                  </MenuItem>
                );
              })}
            </StyledSelect>
            <FormHelperText>
              {getFieldError(userBulkUploadEditErrorList, "premium") ||
                errors.premium}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Designation */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2">
            Designation{" "}
            <span style={{ fontWeight: 400, fontSize: "0.80 rem" }}>
              (optional)
            </span>
          </Typography>
          <FormControl
            fullWidth
            error={
              userBulkUploadEditProfessionalDesignation === "" ||
              !!userBulkUploadEditErrorList?.professional_designation ||
              !!errors.professional_designation
            }>
            <StyledSelect
              name="userBulkUploadEditProfessionalDesignation"
              value={userBulkUploadEditProfessionalDesignation || ""}
              onChange={(event) => {
                handleInputChange(
                  event,
                  "userBulkUploadEditProfessionalDesignation"
                );
              }}
              size="small"
              displayEmpty
              renderValue={(selected) => {
                // try to map code/text to display text
                const mapped = userDesignations.find(
                  (type) => type.code === selected || type.text === selected
                );
                return mapped ? mapped.text : selected; // fallback to raw text
              }}
              disabled={
                enabledFields
                  ? !enabledFields.includes("professional_designation")
                  : false
              }>
              {userDesignations?.map((type) => {
                const text = getCodeText("userDesignation", type.code);
                return (
                  <MenuItem key={type.code} value={text}>
                    {text}
                  </MenuItem>
                );
              })}
            </StyledSelect>
            <FormHelperText>
              {getFieldError(
                userBulkUploadEditErrorList,
                "professional_designation"
              ) || errors.professional_designation}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Stack className="user-edit-discard-form-buttons" direction="row">
        {/* Discard Button */}
        <button
          className="dt-btn-form-action-secondary"
          onClick={() => navigate("/home/users/bulk/upload/step2")}>
          Discard
        </button>

        {/* Save Button */}
        <button
          className="dt-btn-form-action-primary"
          onClick={handleSubmit}
          disabled={userBulkEditSaveLoading || enabledFields?.length === 0}>
          {userBulkEditSaveLoading ? (
            <div className="dot-flashing"></div>
          ) : (
            <>Update</>
          )}
        </button>
      </Stack>
    </Box>
  );
};

export default UserBulkUploadEditForm;
